import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import FlagIcon from "../../ui/icons/FlagIcon";
import { N_A_STRING } from "../../constants";
import {
    planStageEnum,
    planStageTooltipMsg,
} from "../__shared__/RequirementsModal/constants";
import {
    DOWNLOAD_PLAN_TOOLTIPS,
    lockedPlanTooltipMsg,
    patientListPlanStageTooltip,
} from "./constants";
import { getPlanDownloadInfo, planStageToText } from "./helpers";
import LockIcon from "../../ui/icons/LockIcon";
import DownloadIcon from "../../ui/icons/DownloadIcon";
import ArrowIcon from "../../ui/icons/ArrowIcon";

function getLockHolder(plan, useruuid) {
    const locker = plan?.LockedByUser;
    if (locker && locker.uuid !== useruuid) {
        return locker;
    }
    return null;
}

function formatCreationDate(creationDate) {
    const parts = creationDate?.split("-");
    if (parts?.length !== 3) {
        return creationDate;
    }
    const [year, month, day] = parts;
    return `${month}-${day}-${year}`;
}

function renderDownloadPlanTooltip() {
    return (
        <Tooltip
            id="patient-list-item-download-tooltip"
            className="pendo-ignore"
        >
            {DOWNLOAD_PLAN_TOOLTIPS.CEM_GENERATED}
        </Tooltip>
    );
}

function renderCemNotGeneratedTooltip() {
    return (
        <Tooltip
            id="patient-list-item-cem-not-generated-tooltip"
            className="pendo-ignore"
        >
            {DOWNLOAD_PLAN_TOOLTIPS.NO_CEM_GENERATED}
        </Tooltip>
    );
}

function renderLockTooltip(locker) {
    return (
        <Tooltip id="patient-list-item-tooltip" className="pendo-ignore">
            {lockedPlanTooltipMsg(
                locker["firstName"],
                locker["lastName"],
                locker["email"]
            )}
        </Tooltip>
    );
}

function renderFlagTooltip(flagNote) {
    return (
        <Tooltip id="patient-list-item-flag-tooltip" className="pendo-ignore">
            {flagNote}
        </Tooltip>
    );
}

function renderPlanStageTooltip(plan) {
    const planStage = Number(plan?.MetaData?.PlanStage);
    const planStageLastModifiedDate = plan?.MetaData?.PlanStageModifiedDate;

    let tooltipMsg = `${planStageTooltipMsg[planStage]} `;
    tooltipMsg += planStageLastModifiedDate
        ? planStageLastModifiedDate
        : N_A_STRING;

    if (planStage === planStageEnum.NEEDS_DATA) {
        if (!plan?.SavedData?.IsPathReportUploaded && !plan?.SavedData?.PSA) {
            tooltipMsg = patientListPlanStageTooltip.MISSING_ALL;
        } else if (!plan?.SavedData?.IsPathReportUploaded) {
            tooltipMsg = patientListPlanStageTooltip.MISSING_PATH;
        } else if (!plan?.SavedData?.PSA) {
            tooltipMsg = patientListPlanStageTooltip.MISSING_PSA;
        }
    }

    return (
        <Tooltip
            id="patient-list-item-plan-stage-tooltip"
            className="pendo-ignore"
        >
            {tooltipMsg}
        </Tooltip>
    );
}

// TanStack's row.id does not change with sorting, but the UX test selectors (and
// the old design) key off the sorted display position (0 = top) so this function is to keep the original behavior
// TODO: ID Identity should survive sort/filter/paginate. A positional id only tells
//  us "what's currently in this slot" and not "which element is this specific case"
function getRowDisplayIndex(table, row) {
    return table.getRowModel().rows.findIndex((r) => r.id === row.id);
}

function PatientInfoCell({ row, displayIndex, useruuid, onAccessPlan }) {
    const metadata = row.original?.MetaData;

    const lockHolder = getLockHolder(row.original, useruuid);
    const isLocked = lockHolder !== null;

    return (
        <div className="group flex items-start gap-x-2">
            <div className="pt-1">
                {metadata.PlanFlag ? (
                    metadata.PlanFlag.FlagNote ? (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={renderFlagTooltip(
                                metadata.PlanFlag.FlagNote
                            )}
                        >
                            <div>
                                <FlagIcon />
                            </div>
                        </OverlayTrigger>
                    ) : (
                        <FlagIcon />
                    )
                ) : (
                    <FlagIcon className="invisible" />
                )}
            </div>
            <div className="pendo-ignore flex flex-col">
                <div
                    className={`flex gap-x-2 ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={
                        isLocked ? undefined : () => onAccessPlan(row.original)
                    }
                >
                    <span
                        id={`patient-list-item-${displayIndex}`}
                        className={`${isLocked ? "" : "text-primary-700 underline underline-offset-2 group-hover:decoration-2"}`}
                    >
                        {metadata?.FirstName} {metadata?.MiddleName}{" "}
                        {metadata?.LastName}
                    </span>
                    {!isLocked && (
                        <ArrowIcon
                            className={`ml-1 opacity-0 transition-opacity group-hover:opacity-100 ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
                            customColor="var(--primary-700)"
                            direction="right"
                            size={13}
                        />
                    )}
                </div>
                <div className="pendo-ignore text-sm">
                    {metadata?.PatientMRN}
                </div>
            </div>
        </div>
    );
}

function PlanStatusCell({ row, table }) {
    const planStageNumber = Number(row.original?.MetaData?.PlanStage);
    const planStageString = planStageToText(planStageNumber);
    if (!planStageString) {
        return null;
    }

    const isIneligible = planStageNumber === planStageEnum.INELIGIBLE;

    return (
        <OverlayTrigger
            placement="right"
            overlay={renderPlanStageTooltip(row.original)}
        >
            <div
                id={`case-status-badge-${getRowDisplayIndex(table, row)}`}
                className={`pendo-ignore inline-flex h-6 items-center justify-center rounded-lg bg-none px-2 py-1 text-sm font-semibold ${
                    isIneligible
                        ? "text-(--neutral-400)"
                        : "tw-border border-[#c2410c] text-[#c2410c]"
                }`}
            >
                {planStageString}
            </div>
        </OverlayTrigger>
    );
}

function PlanActionsCell({ row, useruuid, onDownloadPlan }) {
    const lockHolder = getLockHolder(row.original, useruuid);
    const isLocked = lockHolder !== null;

    const { isDownloadDisabled } = getPlanDownloadInfo(row.original);

    return (
        <div className="flex justify-end gap-x-10">
            <OverlayTrigger
                overlay={
                    isDownloadDisabled
                        ? renderCemNotGeneratedTooltip()
                        : renderDownloadPlanTooltip()
                }
            >
                <button
                    type="button"
                    className={`outline-none ${
                        isDownloadDisabled
                            ? "cursor-not-allowed"
                            : "cursor-pointer"
                    }`}
                    onClick={
                        isDownloadDisabled
                            ? undefined
                            : () => onDownloadPlan(row.original)
                    }
                >
                    <DownloadIcon
                        customColor={
                            isDownloadDisabled
                                ? "var(--neutral-200)"
                                : undefined
                        }
                    />
                </button>
            </OverlayTrigger>
            <div>
                {isLocked ? (
                    <OverlayTrigger overlay={renderLockTooltip(lockHolder)}>
                        <div>
                            <LockIcon />
                        </div>
                    </OverlayTrigger>
                ) : (
                    <div className="invisible">
                        <LockIcon />
                    </div>
                )}
            </div>
        </div>
    );
}

export const makeColumns = ({ useruuid, onAccessPlan, onDownloadPlan }) => {
    return [
        {
            id: "patient_info",
            header: "Patient Info",
            size: 500,
            accessorFn: (patient) =>
                `${patient.MetaData?.FirstName} ${patient.MetaData?.MiddleName} ${patient.MetaData?.LastName}`,
            cell: ({ row, table }) => (
                <PatientInfoCell
                    row={row}
                    displayIndex={getRowDisplayIndex(table, row)}
                    useruuid={useruuid}
                    onAccessPlan={onAccessPlan}
                />
            ),
        },
        {
            id: "last_modified_date",
            header: "Last Modified Date",
            accessorFn: (patient) =>
                patient.MetaData?.DateLastModified ||
                patient.MetaData?.PlanCreationDate,
            sortingFn: "datetime",
            sortDescFirst: true, // string values default to ascending first via getAutoSortDir()
            cell: ({ getValue }) => formatCreationDate(getValue()),
        },
        {
            id: "case_status",
            header: "Case Status",
            size: 256,
            meta: { align: "center" },
            accessorFn: (patient) =>
                planStageToText(patient.MetaData?.PlanStage),
            cell: PlanStatusCell,
        },
        {
            id: "download_share_and_access_plan",
            header: "Download Case",
            enableResizing: false,
            meta: { align: "right" },
            cell: ({ row }) => (
                <PlanActionsCell
                    row={row}
                    useruuid={useruuid}
                    onDownloadPlan={onDownloadPlan}
                />
            ),
            enableSorting: false,
        },
    ];
};
